// src/components/JournalEntry.js

import React, { useState, useRef } from "react";
import { TextField, Button, Box, Typography, Slider, Card, CardContent, Tabs, Tab, IconButton } from "@mui/material";
import { db, auth } from "../firebase";
import { doc, getDoc } from "firebase/firestore";
import axios from "axios";
import PhotoPopup from "./PhotoPopup";
import DiffPopup from "./DiffPopup"; // Import the DiffPopup component
import './JournalEntry.css';
import MenuBookIcon from '@mui/icons-material/MenuBook';

const JournalEntry = ({
  selectedDate,
  currentEntry,
  setCurrentEntry,
  setLoading,
  setAnalysisResult,
  analysisResult,
  saveEntry,
  openGeoPhotoPopup  // Receive the handler as a prop
}) => {
  const [popupPhotos, setPopupPhotos] = useState(null);
  const [showDiffPopup, setShowDiffPopup] = useState(false); // State to manage DiffPopup visibility
  const [tabIndex, setTabIndex] = useState(0); // Add state for tab switching
  const textFieldRef = useRef(null); // Reference to the text field

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
  };

  const analyzeText = async () => {
    setLoading(true);
    await saveEntry();
    const tokenDoc = await getDoc(doc(db, "tokens", auth.currentUser.uid));
    const chatGptToken = tokenDoc.data().token;
    const response = await axios.post(
      "https://api.openai.com/v1/chat/completions",
      {
        model: "gpt-4o",
        response_format: { "type": "json_object" },
        messages: [
          {
            role: "system",
            content: "You are a helpful assistant that analyzes journal entries, designed to output JSON."
          },
          {
            role: "user",
            content: `Analyze the following journal entry and provide the following details in JSON format: title, corrected (correct any spelling or grammar issues, try to keep the writer style as is), keywords (as an array), mood score from 1 to 5, feedback with suggestions for improvement, and questions to promote further writing. Return a valid JSON format: { "title": "string", "corrected": "string", "keywords": ["string"], "mood": number, "feedback": "string", "questions": ["string"] }.  Here is the journal entry: "${currentEntry.content}"`
          }
        ],
        max_tokens: 3000,
      },
      {
        headers: {
          Authorization: `Bearer ${chatGptToken}`,
        },
      }
    );

    const analysis = JSON.parse(response.data.choices[0].message.content);

    // Update currentEntry conditionally
    setCurrentEntry(prevEntry => ({
      ...prevEntry,
      title: prevEntry.title || analysis.title,
      keywords: prevEntry.keywords.length === 0 ? analysis.keywords : prevEntry.keywords,
      mood: prevEntry.mood === 3 ? analysis.mood : prevEntry.mood,
    }));

    setAnalysisResult(analysis); // Set the analysis result in the App state
    setShowDiffPopup(true); // Show the DiffPopup
    setLoading(false);
  };

  const openPhotoPopup = () => {
    setPopupPhotos(currentEntry.photos);
  };

  const closePhotoPopup = () => {
    setPopupPhotos(null);
  };

  const handleGeoPopupOpen = () => {
    const photosWithGeo = currentEntry.photos.filter(
      photo => photo.latitude && photo.longitude && photo.latitude !== 0 && photo.longitude !== 0
    );
    openGeoPhotoPopup(photosWithGeo);
  };

  const photosWithGeoExist = currentEntry.photos.some(
    photo => photo.latitude && photo.longitude && photo.latitude !== 0 && photo.longitude !== 0
  );

  const handleAccept = () => {
    if (analysisResult) {
      setCurrentEntry(prevEntry => ({
        ...prevEntry,
        content: analysisResult.corrected, // Update the entry with the corrected text
      }));
    }
    setShowDiffPopup(false); // Hide the DiffPopup
  };

  const handleReject = () => {
    setShowDiffPopup(false); // Hide the DiffPopup
  };

  const handlePhotoTimestampClick = (photo) => {
    if (!photo.date_taken) return;
    
    // Extract time from photo timestamp
    const photoDate = new Date(photo.date_taken);
    const hours = photoDate.getHours().toString().padStart(2, '0');
    const minutes = photoDate.getMinutes().toString().padStart(2, '0');
    const timeString = `[${hours}:${minutes}] `;
    
    // Get the current field based on tab
    const currentField = tabIndex === 0 ? 'content' : 'aiRewrite';
    const currentContent = currentEntry?.[currentField] || '';
    
    // Append timestamp to the end of the current content
    const newContent = currentContent + timeString;
    
    // Update the entry
    setCurrentEntry({ ...currentEntry, [currentField]: newContent });
    
    // Focus and scroll to the end of the text field
    setTimeout(() => {
      if (textFieldRef.current) {
        const textField = textFieldRef.current;
        const input = textField.querySelector('textarea') || textField.querySelector('input');
        if (input) {
          input.focus();
          input.setSelectionRange(newContent.length, newContent.length);
          input.scrollTop = input.scrollHeight;
        }
      }
    }, 100);
  };

  return (
    <Card sx={{ mt: 3 }}>
      <CardContent>
        <Typography variant="h5" component="div">
          Journal Entry
        </Typography>
        <Typography color="text.secondary" gutterBottom>
          {currentEntry && currentEntry.createdAt 
            ? currentEntry.createdAt.toDate().toLocaleString() 
            : selectedDate.toLocaleString()}
        </Typography>
        <TextField
          label="Title"
          variant="outlined"
          fullWidth
          value={currentEntry?.title || ""}
          onChange={(e) => setCurrentEntry({ ...currentEntry, title: e.target.value })}
          sx={{ mb: 2 }}
        />
        {/* Tab Navigation */}
        <Tabs value={tabIndex} onChange={handleTabChange} aria-label="journal tabs">
          <Tab label="Original" />
          <Tab label="AI Rewrite" />
        </Tabs>

        {/* Tab Content */}
        {tabIndex === 0 && (
          <TextField
            ref={textFieldRef}
            label="What's on your mind?"
            variant="outlined"
            multiline
            rows={4}
            fullWidth
            value={currentEntry?.content || ""}
            onChange={(e) => setCurrentEntry({ ...currentEntry, content: e.target.value })}
            sx={{ mb: 2 }}
          />
        )}

        {tabIndex === 1 && (
          <TextField
            ref={textFieldRef}
            label="AI Rewrite"
            variant="outlined"
            multiline
            rows={4}
            fullWidth
            value={currentEntry?.aiRewrite || ""}
            onChange={(e) => setCurrentEntry({ ...currentEntry, aiRewrite: e.target.value })}
            sx={{ mb: 2 }}
          />
        )}

        <Box sx={{ mb: 2 }}>
          <Button variant="contained" component="label">
            Upload Image
            <input type="file" hidden onChange={(e) => setCurrentEntry({ ...currentEntry, image: URL.createObjectURL(e.target.files[0]) })} />
          </Button>
          {currentEntry?.image && <img src={currentEntry.image} alt="Journal" style={{ marginTop: 10, maxWidth: "100%" }} />}
        </Box>
        <TextField
          label="Keywords"
          variant="outlined"
          fullWidth
          value={currentEntry?.keywords.join(", ") || ""}
          onChange={(e) => setCurrentEntry({ ...currentEntry, keywords: e.target.value.split(", ") })}
          sx={{ mb: 2 }}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Typography variant="body1" sx={{ mr: 2 }}>Mood:</Typography>
          <Slider
            value={currentEntry?.mood || 3}
            min={1}
            max={5}
            step={1}
            marks
            onChange={(e, newValue) => setCurrentEntry({ ...currentEntry, mood: newValue })}
            valueLabelDisplay="auto"
          />
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button variant="contained" color="secondary" onClick={analyzeText}>
            Analyze Text
          </Button>
          <Button variant="contained" color="primary" onClick={saveEntry}>
            Save
          </Button>
        </Box>
        {currentEntry?.photos?.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" component="div">
              Selected Photos
            </Typography>
            <div className="photos-grid">
              {currentEntry.photos.map((photo, index) => (
                <div key={`selected-${photo.id || index}`} className="photo-item" style={{ position: 'relative' }}> {/* Add position relative */}
                  <img
                    src={photo.baseUrl}
                    alt={photo.filename || 'Journal photo'}
                    style={{ maxWidth: "100px", maxHeight: "100px", margin: "5px" }}
                    onClick={openPhotoPopup} // Keep existing onClick or modify as needed
                  />
                  {/* Notebook icon for adding timestamp */}
                  {photo.date_taken && (
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePhotoTimestampClick(photo);
                      }}
                      sx={{
                        position: 'absolute',
                        top: '2px',
                        right: '2px',
                        backgroundColor: 'rgba(255, 255, 255, 0.8)',
                        color: '#1976d2',
                        width: '20px',
                        height: '20px',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        },
                      }}
                    >
                      <MenuBookIcon fontSize="small" />
                    </IconButton>
                  )}
                  {/* Conditionally render a video icon */}
                  {photo.mimeType && photo.mimeType.startsWith('video/') && (
                    <span style={{
                      position: 'absolute',
                      bottom: '8px', // Adjust position as needed
                      left: '8px',
                      color: 'white',
                      backgroundColor: 'rgba(0, 0, 0, 0.6)',
                      padding: '2px 4px',
                      borderRadius: '3px',
                      fontSize: '0.8em'
                     }}>
                       VIDEO {/* Or use an actual video icon component */}
                     </span>
                  )}
                </div>
              ))}
            </div>
          </Box>
        )}
        {photosWithGeoExist && (
          <Button onClick={handleGeoPopupOpen} variant="contained" color="primary">
            Set Places
          </Button>
        )}
      </CardContent>
      {popupPhotos && (
        <PhotoPopup 
          photos={popupPhotos} 
          closePhotoPopup={closePhotoPopup} 
          currentEntry={currentEntry}
          setCurrentEntry={setCurrentEntry}
        />
      )}
      {showDiffPopup && analysisResult && (
        <DiffPopup 
          original={currentEntry.content}
          corrected={analysisResult.corrected}
          onAccept={handleAccept}
          onReject={handleReject}
        />
      )}
    </Card>
  );
};

export default JournalEntry;
